import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useManagedCategories from "./useManagedCategories";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronDown,
  FaChevronRight,
  FaGlobeAmericas,
  FaHeart,
  FaRegHeart,
  FaShareAlt,
  FaStar,
  FaTimes,
  FaMapMarkerAlt,
  FaSearch,
} from "react-icons/fa";
import {
  HiOutlineBuildingStorefront,
  HiOutlineShoppingBag,
} from "react-icons/hi2";
import useManagedCommunities from "./useManagedCommunities";
import Template from "../main/template";
import Header from "./Header";
import Footer from "./Footer";
import LanguageDropdown from "./LanguageDropdown";
import CommunityConnect from "./CommunityConnect";
import ProductCategories from "./ProductCategories";
import TransitioningSectionForAd from "./TransitioningSectionForAd";
import {
  CATEGORY_META,
  categoryUrl,
  resolveCategoryMeta,
} from "./categoryDirectoryConfig";
import {
  communityUrl,
  PINNED_COMMUNITY_CARDS,
  resolveCommunityMeta,
} from "./communityDirectoryConfig";
import {
  businessCategoryName,
  businessDisplayName,
  loadApprovedBusinesses,
} from "./useBusinessSearch";
import { useAiHubSearch } from "./useAiHubSearch";

import { getData } from "../data/data";
import { useFavorites } from "./FavoritesContext";
import "./CommunityShowcase.css";
import "./Hub.css";
import HubEventsRail from "./HubEventsRail";
import HubOrgs from "./HubOrgs";
import HubQuickActions from "./HubQuickActions";
import HubHero from "./HubHero";
import HubHappening from "./HubHappening";
const HUB_TEXT = {
  english: {
    discoverLocal: "Discover local.",
    intro: "Find businesses, services, and guides tied to the cultures that make the city. Or jump straight to a category.",
    exploreCommunity: "Explore by community",
    browseCategory: "Browse by category",
    chooseCommunity: "Communities"
  },
  spanish: {
    discoverLocal: "Descubre lo local.",
    intro: "Encuentra negocios, servicios y guías relacionados con las culturas que forman la ciudad. O ve directamente a una categoría.",
    exploreCommunity: "Explorar por comunidad",
    browseCategory: "Explorar por categoría",
    chooseCommunity: "Elige una comunidad"
  },
  bengali: {
    discoverLocal: "স্থানীয় আবিষ্কার করুন।",
    intro: "শহরকে গড়ে তোলা সংস্কৃতিগুলোর সঙ্গে যুক্ত ব্যবসা, সেবা ও গাইড খুঁজুন। অথবা সরাসরি একটি বিভাগে যান।",
    exploreCommunity: "কমিউনিটি অনুযায়ী দেখুন",
    browseCategory: "বিভাগ অনুযায়ী দেখুন",
    chooseCommunity: "একটি কমিউনিটি বেছে নিন"
  },
  amharic: {
    discoverLocal: "አካባቢያዊውን ያግኙ።",
    intro: "ከከተማዋ ባህሎች ጋር የተያያዙ ንግዶችን፣ አገልግሎቶችን እና መመሪያዎችን ያግኙ። ወይም በቀጥታ ወደ ምድብ ይሂዱ።",
    exploreCommunity: "በማህበረሰብ ያስሱ",
    browseCategory: "በምድብ ያስሱ",
    chooseCommunity: "ማህበረሰብ ይምረጡ"
  },
  ethiopian: {
    discoverLocal: "አካባቢያዊውን ያግኙ።",
    intro: "ከከተማዋ ባህሎች ጋር የተያያዙ ንግዶችን፣ አገልግሎቶችን እና መመሪያዎችን ያግኙ። ወይም በቀጥታ ወደ ምድብ ይሂዱ።",
    exploreCommunity: "በማህበረሰብ ያስሱ",
    browseCategory: "በምድብ ያስሱ",
    chooseCommunity: "ማህበረሰብ ይምረጡ"
  },
  arabic: {
    discoverLocal: "اكتشف المحلي.",
    intro: "اعثر على الأعمال والخدمات والأدلة المرتبطة بالثقافات التي تشكل المدينة، أو انتقل مباشرة إلى فئة.",
    exploreCommunity: "استكشف حسب المجتمع",
    browseCategory: "تصفح حسب الفئة",
    chooseCommunity: "اختر مجتمعًا"
  },
  chinese: {
    discoverLocal: "发现本地。",
    intro: "查找与城市多元文化相关的商家、服务和指南，或直接进入一个类别。",
    exploreCommunity: "按社区探索",
    browseCategory: "按类别浏览",
    chooseCommunity: "选择社区"
  },
  mandarin: {
    discoverLocal: "发现本地。",
    intro: "查找与城市多元文化相关的商家、服务和指南，或直接进入一个类别。",
    exploreCommunity: "按社区探索",
    browseCategory: "按类别浏览",
    chooseCommunity: "选择社区"
  },
  filipino: {
    discoverLocal: "Tuklasin ang lokal.",
    intro: "Maghanap ng mga negosyo, serbisyo, at gabay na konektado sa mga kulturang bumubuo sa lungsod. O pumunta diretso sa isang kategorya.",
    exploreCommunity: "Mag-explore ayon sa komunidad",
    browseCategory: "Mag-browse ayon sa kategorya",
    chooseCommunity: "Pumili ng komunidad"
  },
  french: {
    discoverLocal: "Découvrez local.",
    intro: "Trouvez des entreprises, des services et des guides liés aux cultures qui façonnent la ville. Ou accédez directement à une catégorie.",
    exploreCommunity: "Explorer par communauté",
    browseCategory: "Parcourir par catégorie",
    chooseCommunity: "Choisissez une communauté"
  },
  gujarati: {
    discoverLocal: "સ્થાનિક શોધો.",
    intro: "શહેરને આકાર આપતી સંસ્કૃતિઓ સાથે જોડાયેલા વ્યવસાયો, સેવાઓ અને માર્ગદર્શિકાઓ શોધો. અથવા સીધા કોઈ શ્રેણીમાં જાઓ.",
    exploreCommunity: "સમુદાય મુજબ શોધો",
    browseCategory: "શ્રેણી મુજબ જુઓ",
    chooseCommunity: "સમુદાય પસંદ કરો"
  },
  german: {
    discoverLocal: "Lokales entdecken.",
    intro: "Finde Unternehmen, Dienstleistungen und Guides zu den Kulturen, die die Stadt prägen. Oder springe direkt zu einer Kategorie.",
    exploreCommunity: "Nach Community entdecken",
    browseCategory: "Nach Kategorie suchen",
    chooseCommunity: "Community auswählen"
  },
  hebrew: {
    discoverLocal: "גלו מקומי.",
    intro: "מצאו עסקים, שירותים ומדריכים הקשורים לתרבויות שמעצבות את העיר, או עברו ישר לקטגוריה.",
    exploreCommunity: "גלו לפי קהילה",
    browseCategory: "עיון לפי קטגוריה",
    chooseCommunity: "בחרו קהילה"
  },
  hindi: {
    discoverLocal: "स्थानीय खोजें।",
    intro: "शहर की संस्कृतियों से जुड़े व्यवसाय, सेवाएँ और गाइड खोजें। या सीधे किसी श्रेणी पर जाएँ।",
    exploreCommunity: "समुदाय के अनुसार खोजें",
    browseCategory: "श्रेणी के अनुसार देखें",
    chooseCommunity: "एक समुदाय चुनें"
  },
  igbo: {
    discoverLocal: "Chọpụta nke dị nso.",
    intro: "Chọta azụmahịa, ọrụ na nduzi jikọrọ na omenala ndị na-eme obodo ahụ. Ma ọ bụ gaa ozugbo na otu ngalaba.",
    exploreCommunity: "Chọgharịa site n’obodo",
    browseCategory: "Chọgharịa site na ngalaba",
    chooseCommunity: "Họrọ obodo"
  },
  italian: {
    discoverLocal: "Scopri il locale.",
    intro: "Trova attività, servizi e guide legati alle culture che rendono unica la città. Oppure vai direttamente a una categoria.",
    exploreCommunity: "Esplora per comunità",
    browseCategory: "Sfoglia per categoria",
    chooseCommunity: "Scegli una comunità"
  },
  japanese: {
    discoverLocal: "地元を発見。",
    intro: "街を形づくる文化に結びついたビジネス、サービス、ガイドを見つけるか、カテゴリへ直接進みます。",
    exploreCommunity: "コミュニティから探す",
    browseCategory: "カテゴリから探す",
    chooseCommunity: "コミュニティを選択"
  },
  korean: {
    discoverLocal: "지역을 발견하세요.",
    intro: "도시를 이루는 문화와 연결된 비즈니스, 서비스, 가이드를 찾거나 카테고리로 바로 이동하세요.",
    exploreCommunity: "커뮤니티별 탐색",
    browseCategory: "카테고리별 탐색",
    chooseCommunity: "커뮤니티 선택"
  },
  oromo: {
    discoverLocal: "Naannoo kee argadhu.",
    intro: "Daldaloota, tajaajiloota fi qajeelfamoota aadaa magaalaa kana ijaaran waliin walqabatan argadhu. Yookaan kallattiin ramaddii tokko seeni.",
    exploreCommunity: "Hawaasa irratti hundaa’uun barbaadi",
    browseCategory: "Ramaddii irratti hundaa’uun ilaali",
    chooseCommunity: "Hawaasa filadhu"
  },
  portuguese: {
    discoverLocal: "Descubra o local.",
    intro: "Encontre negócios, serviços e guias ligados às culturas que formam a cidade. Ou vá direto para uma categoria.",
    exploreCommunity: "Explorar por comunidade",
    browseCategory: "Explorar por categoria",
    chooseCommunity: "Escolha uma comunidade"
  },
  swahili: {
    discoverLocal: "Gundua vya karibu.",
    intro: "Pata biashara, huduma na miongozo inayohusiana na tamaduni zinazounda jiji. Au nenda moja kwa moja kwenye kategoria.",
    exploreCommunity: "Chunguza kwa jamii",
    browseCategory: "Vinjari kwa kategoria",
    chooseCommunity: "Chagua jamii"
  },
  tigrinya: {
    discoverLocal: "ናይ ከባቢ ድለይ።",
    intro: "ምስ ባህልታት እታ ከተማ ዝተኣሳሰሩ ንግድታት፣ ኣገልግሎታትን መምርሒታትን ድለይ። ወይ ብቐጥታ ናብ ምድብ ኪድ።",
    exploreCommunity: "ብማሕበረሰብ ድለይ",
    browseCategory: "ብምድብ ድለይ",
    chooseCommunity: "ማሕበረሰብ ምረጽ"
  },
  wolof: {
    discoverLocal: "Gis li nekk ci sa wet.",
    intro: "Seet liggéeykat, sarwiis ak ndigali yu jëm ci aada yi di tax dëkk bi mel ni mu mel. Wala dem ci benn kategori.",
    exploreCommunity: "Seet ci kominote",
    browseCategory: "Seet ci kategori",
    chooseCommunity: "Tànn kominote"
  }
};

const LIVE_MARKETS = [{ city: "San Antonio", state: "TX" }];

const activeMarketLabel =
  LIVE_MARKETS.length === 1
    ? `Now in ${LIVE_MARKETS[0].city}, ${LIVE_MARKETS[0].state}`
    : `Now live in ${LIVE_MARKETS.length} cities`;

const MODES = {
  COMMUNITY: "community",
  BUSINESS: "business",
};

const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

/* --- mobile discover rails (Popular / New) helpers --- */
const DISCOVER_EMOJI = {
  restaurants: "🍴", restaurant: "🍴", "food truck": "🚚", foodtruck: "🚚",
  groceries: "🛒", grocery: "🛒", "beauty services": "💄", beauty: "💄",
  catering: "🍲", boutiques: "👗", clothing: "👗", photography: "📷",
  realtors: "🏘️", "child care": "👶", "cleaning services": "🧽",
};

const discoverEmoji = (business) =>
  DISCOVER_EMOJI[businessCategoryName(business).toLowerCase()] || "🏪";

/* --- discover thumbnails: real photos before emoji ---
   1. the business's own photo, if the record carries one
   2. a category photo on the Spaces CDN:  img/category/<slug>.jpg
      (slug = category name, lower-case, spaces → "-", e.g.
      "Beauty Services" → beauty-services.jpg). Upload the file and
      it appears; no code change. A missing file falls through to…
   3. the emoji tile (previous look). */
const CATEGORY_PHOTO_BASE =
  "https://goourhub.sfo2.digitaloceanspaces.com/img/category/";

const CATEGORY_PHOTO_ALIASES = {
  restaurant: "restaurants",
  grocery: "groceries",
  beauty: "beauty-services",
  clothing: "boutiques",
  foodtruck: "food-truck",
};

const categoryPhotoSlug = (business) => {
  const slug = businessCategoryName(business)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return CATEGORY_PHOTO_ALIASES[slug] || slug;
};

const firstImageValue = (value) => {
  if (Array.isArray(value)) return firstImageValue(value[0]);
  if (value && typeof value === "object") {
    return firstImageValue(value.url || value.src || value.path || "");
  }
  const text = typeof value === "string" ? value.trim() : "";
  return text.startsWith("[") ? "" : text.split(",")[0]?.trim() || "";
};

const businessPhoto = (business) => {
  const raw = firstText(
    firstImageValue(business?.image_url),
    firstImageValue(business?.cover_image),
    firstImageValue(business?.cover),
    firstImageValue(business?.banner),
    firstImageValue(business?.photo),
    firstImageValue(business?.photos),
    firstImageValue(business?.images),
    firstImageValue(business?.image),
    firstImageValue(business?.logo_url),
    firstImageValue(business?.logo)
  );
  if (!raw) return "";
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
  return raw;
};

const discoverImage = (business) => {
  const own = businessPhoto(business);
  if (own) return own;
  const slug = categoryPhotoSlug(business);
  return slug ? `${CATEGORY_PHOTO_BASE}${slug}.jpg` : "";
};

/* photo when one loads, emoji when none does (or the CDN file is missing) */
function DiscoverThumb({ business, className, children }) {
  const src = discoverImage(business);
  const [failed, setFailed] = useState(false);
  const showPhoto = Boolean(src) && !failed;

  return (
    <span className={`${className}${showPhoto ? " has-photo" : ""}`} aria-hidden="true">
      {showPhoto ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        discoverEmoji(business)
      )}
      {children}
    </span>
  );
}

const discoverTime = (business) => {
  const raw = firstText(
    business?.submitted_at,
    business?.submittedAt,
    business?.created_at,
    business?.createdAt
  );
  const time = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(time) ? time : 0;
};

const discoverPremium = (business) =>
  /premium|paid/i.test(
    firstText(business?.listing_plan, business?.listingPlan, business?.plan)
  ) || business?.legacy_premium_label === true;

const SPECIAL_DIRECTORY_PATHS = new Set([
  "/farmersmarket",
  "/fleamarketplace",
  "/murals",
  "/wreaths",
  "/clothing",
  "/toys",
]);


/*
 * Only values already configured as real GoOurHub
 * business categories may appear in the Hub category grid.
 *
 * This prevents imported country/community values such as
 * Brazil, Colombia, Cuba, France, Germany, etc. from being
 * displayed as business categories.
 */
const ALLOWED_DIRECTORY_CATEGORY_SLUGS = new Set(
  CATEGORY_META.map((item) => {
    const meta = resolveCategoryMeta(
      item.value ||
        item.slug ||
        item.label ||
        item.name
    );

    return meta.slug;
  }).filter(Boolean)
);
// Matches the mobile breakpoint in Hub.css.
const isMobileView = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 720px)").matches;

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const categoryNameFromItem = (item) => {
  if (typeof item === "string") {
    return item;
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  return firstText(
    item.value,
    item.category,
    item.slug,
    item.name,
    item.label,
    item.key
  );
};

const categoryCountFromItem = (item) => {
  if (!item || typeof item !== "object") {
    return 0;
  }

  const count = Number(
    item.count ??
      item.business_count ??
      item.businessCount ??
      item.total ??
      item.totalBusinesses ??
      0
  );

  return Number.isFinite(count) && count >= 0 ? count : 0;
};

const normalizeDirectoryCategories = (payload) => {
  const rawCategories = Array.isArray(payload?.categories)
    ? payload.categories
    : payload?.categories && typeof payload.categories === "object"
      ? Object.entries(payload.categories).map(([value, count]) => ({
          value,
          count,
        }))
      : [];

  const uniqueCategories = new Map();

  rawCategories.forEach((item) => {
  const rawName = categoryNameFromItem(item);

  if (!rawName) {
    return;
  }

  const meta = resolveCategoryMeta(rawName);

  /*
   * Do not allow random values stored in businesses.category
   * to become category cards.
   */
  if (
    !meta?.slug ||
    !ALLOWED_DIRECTORY_CATEGORY_SLUGS.has(meta.slug)
  ) {
    return;
  }

  const count = categoryCountFromItem(item);
  const existing = uniqueCategories.get(meta.slug);

  uniqueCategories.set(meta.slug, {
    ...meta,
    count: Math.max(
      existing?.count || 0,
      count
    ),
  });
});

  /* most-populated first (Local Restaurants, Food Trucks, Grocery…),
     alphabetical among equals, so the two-row / six-tile previews show
     the categories people actually use */
  return Array.from(uniqueCategories.values()).sort(
    (left, right) =>
      right.count - left.count ||
      left.label.localeCompare(right.label, undefined, { sensitivity: "base" })
  );
};

const communityNameFromItem = (item) => {
  if (typeof item === "string") {
    return item;
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  return firstText(
    item.value,
    item.community,
    item.name,
    item.label,
    item.slug,
    item.key
  );
};

const communityCountFromItem = (item) => {
  if (!item || typeof item !== "object") {
    return 0;
  }

  const count = Number(
    item.count ??
      item.business_count ??
      item.businessCount ??
      item.total ??
      item.totalBusinesses ??
      0
  );

  return Number.isFinite(count) && count >= 0 ? count : 0;
};

const normalizeDirectoryCommunities = (payload) => {
  const rawCommunities = Array.isArray(payload?.communities)
    ? payload.communities
    : payload?.communities && typeof payload.communities === "object"
      ? Object.entries(payload.communities).map(([value, count]) => ({
          value,
          count,
        }))
      : [];

  const uniqueCommunities = new Map();

  rawCommunities.forEach((item) => {
    const rawName = communityNameFromItem(item);

    if (!rawName) {
      return;
    }

    const meta = resolveCommunityMeta(rawName);
    const count = communityCountFromItem(item);
    const existing = uniqueCommunities.get(meta.slug);

    uniqueCommunities.set(meta.slug, {
      ...meta,
      count: Math.max(existing?.count || 0, count),
    });
  });

  return Array.from(uniqueCommunities.values()).sort((left, right) =>
    left.label.localeCompare(right.label, undefined, {
      sensitivity: "base",
    })
  );
};

function SpotlightRail() {
  return (
    <aside className="hub-rail" aria-label="Local spotlight and links">
      <section className="hub-rail-card" aria-label="Local spotlight">
        <div className="hub-rail-card__head">
          <span className="hub-kicker">Local spotlight</span>
          <p>Featured offers from businesses near you.</p>
        </div>

        <div className="hub-rail-card__media">
          <TransitioningSectionForAd />
        </div>
      </section>

      <HubQuickActions />
    </aside>
  );
}

// Reused for the desktop top-row card and the mobile relocated banner.
function MarketCard({ className = "" }) {
  return (
    <Link
      to="/HubMart"
      className={`hub-choice__card hub-choice__card--market ${className}`}
      aria-label="Shop the HubMart marketplace   buy and sell products, services, and deals"
    >
      <span className="hub-choice__market-glow" aria-hidden="true" />

      <span className="hub-choice__icon" aria-hidden="true">
        <HiOutlineShoppingBag />
      </span>

      <span className="hub-choice__text">
        <span className="hub-choice__title">Shop HubMart</span>
        <span className="hub-choice__desc">
          Buy and sell products and deals
        </span>
      </span>

      <span className="hub-choice__market-arrow" aria-hidden="true">
        <FaArrowRight />
      </span>
    </Link>
  );
}

export default function Hub() {
  const [language, setLanguage] = useState("english");
  const hubText = HUB_TEXT[language] || HUB_TEXT.english;
  const searchQuery = "";
  const [aiQuery, setAiQuery] = useState("");
  const [mode, setMode] = useState(MODES.COMMUNITY);
  const {
  categories: managedCategories,
  loaded: managedCategoriesLoaded,
  loading: managedCategoriesLoading,
  error: managedCategoriesError,
  reload: reloadManagedCategories,
} = useManagedCategories(API_BASE_URL);
  const [directoryCategories, setDirectoryCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState("");
  const [categoryReloadNumber, setCategoryReloadNumber] = useState(0);

  const [directoryCommunities, setDirectoryCommunities] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(true);
  const [communitiesError, setCommunitiesError] = useState("");
  const [communityReloadNumber, setCommunityReloadNumber] = useState(0);
const {
  communities: managedCommunities,
  loaded: managedCommunitiesLoaded,
} = useManagedCommunities();
  const searchRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  /* AI is request-based, never called while the visitor is merely typing.
     One interpretation call then searches every relevant live Hub source. */
  const {
    status: aiSearchState,
    results: aiResults,
    message: aiSearchMessage,
    filters: aiSearchFilters,
    error: aiSearchError,
    search: runAiSearch,
    reset: resetAiSearch,
  } = useAiHubSearch();

  /* Mobile (≤720px) shows BOTH sections stacked with previews;
     desktop keeps the original tabbed panels untouched. */
  const [isMobile, setIsMobile] = useState(isMobileView);
  const [showAllCommunities, setShowAllCommunities] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  /* Desktop shows TWO ROWS of each grid, then "View all". The number
     of tiles per row depends on the window width, so it is measured
     from the real grid (communities: auto-fill minmax(180px) + 10px
     gap, see Hub.css; categories: the rendered tile width). */
  const categoryPanelRef = useRef(null);
  const [communityCols, setCommunityCols] = useState(0);
  const [categoryCols, setCategoryCols] = useState(0);
  const COMMUNITY_PREVIEW = 6; // 6 tiles = 2 rows of 3 on phones; "View all" expands

  /* Mobile discover rails: one cached fetch, shared with search */
  const [discoverList, setDiscoverList] = useState([]);

  useEffect(() => {
    if (!isMobile || discoverList.length > 0) {
      return undefined;
    }

    let alive = true;

    loadApprovedBusinesses().then(({ list }) => {
      if (alive && Array.isArray(list)) {
        setDiscoverList(list);
      }
    });

    return () => {
      alive = false;
    };
  }, [isMobile, discoverList.length]);

  const popularBusinesses = useMemo(() => {
    const sorted = [...discoverList].sort((a, b) => {
      const premiumDiff =
        Number(discoverPremium(b)) - Number(discoverPremium(a));
      return premiumDiff !== 0
        ? premiumDiff
        : discoverTime(b) - discoverTime(a);
    });
    return sorted.slice(0, 4);
  }, [discoverList]);

  const { isSaved, toggleSave } = useFavorites();

  /* details card same content as the listing modal used
     inside communities/categories */
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  useEffect(() => {
    if (!selectedBusiness) {
      return undefined;
    }

    const onKey = (event) => {
      if (event.key === "Escape") {
        setSelectedBusiness(null);
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedBusiness]);

  const sheetProfilePath = firstText(
    selectedBusiness?.profile_path,
    selectedBusiness?.profilePath
  );

  const sheetWebsiteRaw = firstText(selectedBusiness?.website);
  const sheetWebsite = sheetWebsiteRaw
    ? /^https?:\/\//i.test(sheetWebsiteRaw)
      ? sheetWebsiteRaw
      : `https://${sheetWebsiteRaw}`
    : "";

  const newestBusinesses = useMemo(
    () =>
      [...discoverList]
        .sort((a, b) => discoverTime(b) - discoverTime(a))
        .slice(0, 3),
    [discoverList]
  );

  useEffect(() => {
    const query = window.matchMedia("(max-width: 720px)");
    const onChange = () => setIsMobile(query.matches);

    if (query.addEventListener) {
      query.addEventListener("change", onChange);
    } else {
      query.addListener(onChange);
    }

    return () => {
      if (query.removeEventListener) {
        query.removeEventListener("change", onChange);
      } else {
        query.removeListener(onChange);
      }
    };
  }, []);

  const languageData = getData(language);

  const { categories: translatedCategoryTiles } = languageData;

  // English community cards come from the approved-business database.
  // Existing translated community lists remain available for other languages.
 const liveCommunityCards = useMemo(() => {
  const merged = new Map();

  const managedBySlug = new Map();

  managedCommunities.forEach((item) => {
    const meta = resolveCommunityMeta(
      item.value ||
        item.label ||
        item.slug
    );

    const slug =
      item.slug ||
      meta.slug;

    managedBySlug.set(
      slug,
      item
    );
  });

  /*
   * Communities that already have approved businesses.
   */
  directoryCommunities.forEach((item) => {
    const meta = resolveCommunityMeta(
      item.value ||
        item.label ||
        item.slug
    );

    const managed =
      managedBySlug.get(meta.slug);

    /*
     * When Admin explicitly turns "Show on Hub" off,
     * hide the card even if businesses exist.
     */
   

    merged.set(meta.slug, {
      people:
        managed?.label ||
        item.label ||
        meta.label,

      value:
        managed?.value ||
        item.value ||
        meta.value,

      flag:
        managed?.flag ||
        item.flag ||
        meta.flag,

      icon:
        managed?.icon ||
        item.icon ||
        meta.icon,

      count:
        Number(item.count || 0),

      url:
        managed?.url ||
        `/communities/${
          managed?.slug ||
          meta.slug
        }`,

      comingSoon: false,
    });
  });

  /*
   * When the managed API loaded successfully,
   * Admin controls supplemental Hub communities.
   *
   * If it fails, keep the existing hardcoded
   * PINNED_COMMUNITY_CARDS as a safety fallback.
   */
  const supplemental =
    managedCommunitiesLoaded
      ? managedCommunities.filter(
          (item) =>
            item.showOnHub === true
        )
      : PINNED_COMMUNITY_CARDS;

  supplemental.forEach((item) => {
    const meta =
      resolveCommunityMeta(
        item.value ||
          item.people ||
          item.label ||
          item.slug
      );

    const slug =
      item.slug ||
      meta.slug;

    /*
     * A community with approved businesses was
     * already added above.
     */
    if (merged.has(slug)) {
      return;
    }

    merged.set(slug, {
      people:
        item.label ||
        item.people ||
        meta.label,

      value:
        item.value ||
        meta.value,

      flag:
        item.flag ||
        meta.flag,

      icon:
        item.icon ||
        meta.icon,

      url:
        item.url ||
        `/communities/${slug}`,

      count: 0,

      comingSoon:
        item.comingSoon === true,
    });
  });

  return Array.from(
    merged.values()
  ).sort((first, second) =>
    String(
      first.people || ""
    ).localeCompare(
      String(
        second.people || ""
      ),
      undefined,
      {
        sensitivity: "base",
      }
    )
  );
}, [
  directoryCommunities,
  managedCommunities,
  managedCommunitiesLoaded,
]);

const comingSoonCommunityCount =
  liveCommunityCards.filter(
    (community) =>
      community.comingSoon
  ).length;

  const frequentlyVisitedCommunities =
    language === "english"
      ? liveCommunityCards
      : languageData.frequentlyVisitedCommunities || [];

  const communities =
    language === "english" ? [] : languageData.communities || [];

  const specialDirectoryTiles = useMemo(() => {
    if (!Array.isArray(translatedCategoryTiles)) {
      return [];
    }

    return translatedCategoryTiles.filter((item) => {
      const route = firstText(item?.url).toLowerCase();
      return SPECIAL_DIRECTORY_PATHS.has(route);
    });
  }, [translatedCategoryTiles]);

  const liveCategoryTiles = useMemo(() => {
    const sourceCategories =
      directoryCategories.length > 0 ? directoryCategories : CATEGORY_META;

    return sourceCategories.map((item) => {
      const meta = resolveCategoryMeta(item.value || item.slug || item.label);

      return {
        name: meta.label,
        icon: meta.icon,
        url: categoryUrl(meta.value),
        count: Number(item.count || 0),
      };
    });
  }, [directoryCategories]);

  /* The API only returns categories that currently have approved
     businesses. "View all" must show the COMPLETE directory (what the
     /categories page shows), so the live tiles come first, unchanged,
     and every other known category follows. */
  const allCategoryTiles = useMemo(() => {
  /*
   * Preserve existing category cards, URLs, and business counts.
   */
  const tiles = new Map(
    liveCategoryTiles
      .filter((tile) => tile.url)
      .map((tile) => [tile.url, tile])
  );

  /*
   * Keep existing directory entries during this transition.
   * Do not make old categories disappear.
   */
  CATEGORY_META.forEach((item) => {
    const meta = resolveCategoryMeta(
      item.value || item.slug || item.label || item.name
    );

    const url = categoryUrl(meta.value);

    if (!tiles.has(url)) {
      tiles.set(url, {
        name: meta.label,
        icon: meta.icon,
        url,
        count: 0,
      });
    }
  });

  /*
   * Add categories managed through Business Admin,
   * including categories with no businesses yet.
   */
  if (managedCategoriesLoaded) {
    managedCategories.forEach((item) => {
      // "Multiple" is a form choice, not a browse category.
      if (!item.value || item.value === "multiple") {
        return;
      }

      const fallback = resolveCategoryMeta(item.value);

      // Match existing cards by their established category URL.
      const key = categoryUrl(item.value);
      const previous = tiles.get(key);

      tiles.set(key, {
        ...previous,
        value: item.value,

        name:
          item.value === "others"
            ? "Other Businesses"
            : item.name || fallback.label,

        icon:
          item.icon ||
          previous?.icon ||
          fallback.icon,

        // Preserve existing links; use the managed slug for new ones.
        url:
          previous?.url ||
          `/categories/${encodeURIComponent(
            item.slug || fallback.slug
          )}`,

        count: Number(previous?.count || 0),
      });
    });
  }

  return Array.from(tiles.values()).sort(
    (first, second) =>
      Number(second.count > 0) - Number(first.count > 0) ||
      String(first.name || "").localeCompare(
        String(second.name || ""),
        undefined,
        { sensitivity: "base" }
      )
  );
}, [
  liveCategoryTiles,
  managedCategories,
  managedCategoriesLoaded,
]);




  const aiTypes = useMemo(
    () =>
      new Set(
        Array.isArray(aiSearchFilters?.resultTypes)
          ? aiSearchFilters.resultTypes
          : []
      ),
    [aiSearchFilters]
  );

  const aiCommunities = useMemo(() => {
    if (aiSearchState !== "ready" || !aiTypes.has("communities")) return [];

    const terms = [
      aiSearchFilters?.community,
      ...(Array.isArray(aiSearchFilters?.keywords)
        ? aiSearchFilters.keywords
        : []),
    ]
      .map((value) => firstText(value).toLowerCase())
      .filter(Boolean);

    const candidates = liveCommunityCards.map((item) => ({
      name: firstText(item.people, item.value),
      url: firstText(item.url),
    }));

    if (terms.length === 0) return candidates.slice(0, 4);

    return candidates
      .filter((item) =>
        terms.some((term) => item.name.toLowerCase().includes(term))
      )
      .slice(0, 4);
  }, [aiSearchFilters, aiSearchState, aiTypes, liveCommunityCards]);

  const aiCategories = useMemo(() => {
    if (aiSearchState !== "ready" || !aiTypes.has("businesses")) return [];
    const wanted = firstText(aiSearchFilters?.category).toLowerCase();
    if (!wanted) return [];
    return allCategoryTiles
      .filter((tile) => tile.name.toLowerCase().includes(wanted))
      .slice(0, 2);
  }, [aiSearchFilters, aiSearchState, aiTypes, allCategoryTiles]);

  const aiBusinesses = aiResults?.businesses || [];
  const aiEvents = aiResults?.events || [];
  const aiDeals = aiResults?.deals || [];
  const aiOrganizations = aiResults?.organizations || [];
  const aiHubMart = aiResults?.hubmart || [];
  const aiResultCount =
    aiBusinesses.length +
    aiEvents.length +
    aiDeals.length +
    aiOrganizations.length +
    aiHubMart.length +
    aiCommunities.length +
    aiCategories.length;

  const matchingSpecialDirectoryTiles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return specialDirectoryTiles;
    }

    return specialDirectoryTiles.filter((item) =>
      firstText(item?.name).toLowerCase().includes(query)
    );
  }, [searchQuery, specialDirectoryTiles]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCommunities = async () => {
      setCommunitiesLoading(true);
      setCommunitiesError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/directory/communities`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        let payload;

        try {
          payload = await response.json();
        } catch {
          throw new Error(
            "The community directory returned an invalid response."
          );
        }

        if (!response.ok || payload?.success === false) {
          throw new Error(
            payload?.message ||
              `Unable to load communities (${response.status}).`
          );
        }

        const normalizedCommunities =
          normalizeDirectoryCommunities(payload);

        if (normalizedCommunities.length === 0) {
          throw new Error(
            "No available business communities were returned."
          );
        }

        if (!controller.signal.aborted) {
          setDirectoryCommunities(normalizedCommunities);
        }
      } catch (error) {
        if (error.name !== "AbortError" && !controller.signal.aborted) {
          console.error("Unable to load live communities.", error);
          setDirectoryCommunities([]);
          setCommunitiesError(
            error.message ||
              "Live communities could not be loaded. Please try again."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setCommunitiesLoading(false);
        }
      }
    };

    loadCommunities();

    return () => controller.abort();
  }, [communityReloadNumber]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/directory/categories`,
          {
            method: "GET",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          }
        );

        let payload;

        try {
          payload = await response.json();
        } catch {
          throw new Error("The category directory returned an invalid response.");
        }

        if (!response.ok || payload?.success === false) {
          throw new Error(
            payload?.message ||
              `Unable to load business categories (${response.status}).`
          );
        }

        const normalizedCategories = normalizeDirectoryCategories(payload);

        if (normalizedCategories.length === 0) {
          throw new Error("No approved business categories were returned.");
        }

        if (!controller.signal.aborted) {
          setDirectoryCategories(normalizedCategories);
        }
      } catch (error) {
        if (error.name !== "AbortError" && !controller.signal.aborted) {
          console.error("Unable to load live business categories.", error);
          setDirectoryCategories([]);
          setCategoriesError(
            error.message ||
              "Live categories could not be loaded. Saved category links are shown instead."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories();

    return () => controller.abort();
  }, [categoryReloadNumber]);

  const handleHubSearchSubmit = (rawQuery) => {
    const query = String(rawQuery || "").trim();

    if (!query) {
      return;
    }

    navigate(
      `/hub/search?q=${encodeURIComponent(query)}&lang=${encodeURIComponent(
        language
      )}`
    );
  };

  const handleAiSearchSubmit = (rawQuery) => {
    const query = String(rawQuery || "").trim();

    if (query.length < 3) {
      searchRef.current?.focus();
      return;
    }

    runAiSearch(query);
  };

  const handleAiQueryChange = (event) => {
    setAiQuery(event.target.value);
    resetAiSearch();
  };

  const clearAiSearch = () => {
    setAiQuery("");
    resetAiSearch();
    searchRef.current?.focus();
  };

  const openAiEvent = (event) => {
    window.dispatchEvent(
      new CustomEvent("gohub:open-event", { detail: { event } })
    );
  };

  const openAiDeal = (item) => {
    window.dispatchEvent(
      new CustomEvent("gohub:open-happening", { detail: { item } })
    );
  };

  const openAiOrganization = (organization) => {
    window.dispatchEvent(
      new CustomEvent("gohub:open-organization", {
        detail: { organization },
      })
    );
  };

  const openAiHubMartItem = (item) => {
    const id = firstText(item?.id, item?.postid, item?.postId, item?._id?.$oid);
    if (!id) {
      navigate("/HubMart");
      return;
    }

    try {
      sessionStorage.setItem(`hb:item:${id}`, JSON.stringify(item));
    } catch {
      // The HubMart page can still load its own record.
    }

    navigate(`/HubMart#/item/${encodeURIComponent(id)}`);
  };

  /* Tapping a business result: go to its category listing page,
     falling back to the sitewide search. */
  const openBusinessResult = (business) => {
    /* Same behavior as inside communities/categories: the
       details card opens first; its buttons carry you on. */
    setSelectedBusiness(business);
  };

  const openBusinessMap = (business) => {
    const direct = firstText(business?.map_url, business?.mapUrl);

    if (direct) {
      window.open(direct, "_blank", "noopener");
      return;
    }

    const query =
      firstText(business?.address, business?.full_address) ||
      `${businessDisplayName(business)} San Antonio`;

    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener"
    );
  };

  const shareBusinessFromHub = async (business) => {
    const text = `${businessDisplayName(business)} on GoOurHub`;
    const url = window.location.origin;

    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url });
        return;
      }
    } catch {
      return;
    }

    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      window.alert("Link copied.");
    } catch {
      window.alert(url);
    }
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    const mobile = isMobileView();

    window.setTimeout(() => {
      // On mobile, jump straight to the selected panel content.
      if (mobile && panelRef.current) {
        panelRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

    }, 110);
  };

  const goToPartnerSignup = () => {
    navigate("/partner-signup");
  };

  useLayoutEffect(() => {
    if (isMobile) return undefined;

    const measure = () => {
      const panel = panelRef.current;
      if (panel) {
        const cs = window.getComputedStyle(panel);
        const inner =
          panel.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        setCommunityCols(Math.max(1, Math.floor((inner + 10) / 190)));
      }

      const catPanel = categoryPanelRef.current;
      const container = catPanel?.querySelector(
        ".new-tile-container, .new-tile-container-new"
      );
      const tile = container?.querySelector(".new-tile");
      if (container && tile) {
        const ccs = window.getComputedStyle(container);
        const tcs = window.getComputedStyle(tile);
        const inner =
          container.clientWidth -
          parseFloat(ccs.paddingLeft) -
          parseFloat(ccs.paddingRight);
        const gap = parseFloat(ccs.columnGap) || 0;
        const outer =
          tile.getBoundingClientRect().width +
          parseFloat(tcs.marginLeft) +
          parseFloat(tcs.marginRight);
        if (outer > 0) {
          setCategoryCols(Math.max(1, Math.floor((inner + gap) / (outer + gap))));
        }
      }
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isMobile, frequentlyVisitedCommunities.length, liveCategoryTiles.length]);

  const communityPreviewCount = isMobile
    ? COMMUNITY_PREVIEW
    : (communityCols || 6) * 2;
  const communityCollapsible =
    frequentlyVisitedCommunities.length > communityPreviewCount;
  const shownCommunities =
    communityCollapsible && !showAllCommunities
      ? frequentlyVisitedCommunities.slice(0, communityPreviewCount)
      : frequentlyVisitedCommunities;

  const categoryPreviewCount = isMobile ? 6 : (categoryCols || 6) * 2;
  const categoryCollapsible =
    !searchQuery.trim() && allCategoryTiles.length > categoryPreviewCount;
  const shownCategoryTiles = searchQuery.trim()
    ? allCategoryTiles.filter((tile) =>
        String(tile.name || "")
          .toLowerCase()
          .includes(searchQuery.trim().toLowerCase())
      )
    : categoryCollapsible && !showAllCategories
      ? allCategoryTiles.slice(0, categoryPreviewCount)
      : allCategoryTiles;

  return (
    <div className="App hub-app">
      <Template variant="hub">
        <div className="headerSection">
          <Header enableHeaderSearch onSearchSubmit={handleHubSearchSubmit} />
        </div>

        <div className="mainSection">
          <main className="hub-page">
            {/* 1 · hero: one message, two doors, skyline on the right */}
            <HubHero />

            {/* One AI doorway for the entire Hub. The model interprets the
                request once; results still come only from GoOurHub sources. */}
            <section className="hub-ai-shell" aria-labelledby="hub-ai-title">
              <div className="hub-ai-shell__head">
                <span className="hub-ai-shell__spark" aria-hidden="true">✦</span>
                <div>
                  <span className="hub-ai-intro__badge">AI beta</span>
                  <h2 id="hub-ai-title">What can we help you find?</h2>
                  <p>
                    Search businesses, communities, events, deals, nonprofits,
                    and HubMart in one place.
                  </p>
                </div>
              </div>

              <form
                className="hub-search hub-search--universal"
                role="search"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAiSearchSubmit(aiQuery);
                }}
              >
                <div className="hub-search__field">
                  <FaSearch className="hub-search__icon" aria-hidden="true" />
                  <input
                    ref={searchRef}
                    id="hub-ai-search"
                    type="text"
                    className="hub-search__input"
                    value={aiQuery}
                    onChange={handleAiQueryChange}
                    placeholder='Try: "Free family events this weekend"'
                    aria-label="Ask AI to search all of GoOurHub"
                    autoComplete="off"
                  />
                  {aiQuery && (
                    <button
                      type="button"
                      className="hub-search__clear"
                      onClick={clearAiSearch}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="hub-search__ai"
                  disabled={
                    aiQuery.trim().length < 3 || aiSearchState === "loading"
                  }
                >
                  <span aria-hidden="true">✦</span>
                  {aiSearchState === "loading" ? "Thinking…" : "Ask AI"}
                </button>
              </form>

              <div
                className="hub-search-more hub-search-more--universal"
                aria-live="polite"
                aria-busy={aiSearchState === "loading"}
              >
                {aiSearchState === "loading" && (
                  <div className="hub-ai-status" role="status">
                    <span className="hub-ai-status__spark" aria-hidden="true">✦</span>
                    <div>
                      <strong>Understanding your request…</strong>
                      <span>Checking the live GoOurHub sections.</span>
                    </div>
                  </div>
                )}

                {aiSearchState === "error" && (
                  <div className="hub-ai-error" role="alert">
                    {aiSearchError}
                    <button
                      type="button"
                      onClick={() => handleHubSearchSubmit(aiQuery)}
                    >
                      Use regular search
                    </button>
                  </div>
                )}

                {aiSearchState === "ready" && (
                  <div className="hub-ai-results hub-ai-results--universal">
                    <div className="hub-ai-results__summary">
                      <span className="hub-ai-results__label">
                        <span aria-hidden="true">✦</span> Hub matches
                      </span>
                      <p>{aiSearchMessage}</p>
                      <div className="hub-ai-results__filters" aria-label="AI search filters">
                        {[
                          aiSearchFilters?.community,
                          aiSearchFilters?.category,
                          aiSearchFilters?.hubmartCategory,
                          aiSearchFilters?.eventTiming !== "any"
                            ? aiSearchFilters?.eventTiming?.replace(/_/g, " ")
                            : "",
                          aiSearchFilters?.freeOnly ? "free" : "",
                        ]
                          .filter(Boolean)
                          .map((filter, index) => (
                            <span key={`${filter}-${index}`}>{filter}</span>
                          ))}
                      </div>
                    </div>

                    {aiCommunities.length > 0 && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">🌍</span> Communities</h3>
                        <div className="hub-ai-group__grid">
                          {aiCommunities.map((community) => (
                            <Link key={community.url} to={community.url} className="hub-ai-result">
                              <span><strong>{community.name}</strong><small>Explore community</small></span>
                              <FaArrowRight aria-hidden="true" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {(aiBusinesses.length > 0 || aiCategories.length > 0) && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">🏪</span> Businesses</h3>
                        <div className="hub-ai-group__grid">
                          {aiCategories.map((category) => (
                            <Link key={category.url} to={category.url} className="hub-ai-result hub-ai-result--browse">
                              <span><strong>Browse {category.name}</strong><small>Business category</small></span>
                              <FaArrowRight aria-hidden="true" />
                            </Link>
                          ))}
                          {aiBusinesses.map((business, index) => (
                            <button
                              key={business.id || `${businessDisplayName(business)}-${index}`}
                              type="button"
                              className="hub-ai-result"
                              onClick={() => openBusinessResult(business)}
                            >
                              <span>
                                <strong>{businessDisplayName(business) || "Unnamed business"}</strong>
                                <small>{[businessCategoryName(business), firstText(business?.city)].filter(Boolean).join(" · ")}</small>
                              </span>
                              <FaArrowRight aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiEvents.length > 0 && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">📅</span> Events</h3>
                        <div className="hub-ai-group__grid">
                          {aiEvents.map((event, index) => (
                            <button
                              key={event.id || `${event.title}-${index}`}
                              type="button"
                              className="hub-ai-result"
                              onClick={() => openAiEvent(event)}
                            >
                              <span><strong>{event.title || "Local event"}</strong><small>{[event.event_date, event.venue_name || event.city].filter(Boolean).join(" · ")}</small></span>
                              <FaArrowRight aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiDeals.length > 0 && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">🏷️</span> Deals</h3>
                        <div className="hub-ai-group__grid">
                          {aiDeals.map((deal, index) => (
                            <button
                              key={deal.key || `${deal.title}-${index}`}
                              type="button"
                              className="hub-ai-result"
                              onClick={() => openAiDeal(deal)}
                            >
                              <span><strong>{deal.title}</strong><small>{[deal.badge, deal.place].filter(Boolean).join(" · ")}</small></span>
                              <FaArrowRight aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiOrganizations.length > 0 && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">💜</span> Nonprofits &amp; organizations</h3>
                        <div className="hub-ai-group__grid">
                          {aiOrganizations.map((organization, index) => (
                            <button
                              key={organization.id || organization.slug || `${organization.name}-${index}`}
                              type="button"
                              className="hub-ai-result"
                              onClick={() => openAiOrganization(organization)}
                            >
                              <span><strong>{organization.name || "Community organization"}</strong><small>{organization.communityLabel || "Local organization"}</small></span>
                              <FaArrowRight aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiHubMart.length > 0 && (
                      <div className="hub-ai-group">
                        <h3><span aria-hidden="true">🛍️</span> HubMart</h3>
                        <div className="hub-ai-group__grid">
                          {aiHubMart.map((item, index) => (
                            <button
                              key={item.id || `${item.title}-${index}`}
                              type="button"
                              className="hub-ai-result"
                              onClick={() => openAiHubMartItem(item)}
                            >
                              <span>
                                <strong>{item.title || "HubMart listing"}</strong>
                                <small>{[
                                  Number(item.price) > 0 ? `$${Number(item.price).toFixed(2)}` : "Free",
                                  item.location || item.city,
                                ].filter(Boolean).join(" · ")}</small>
                              </span>
                              <FaArrowRight aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {aiResultCount === 0 && (
                      <p className="hub-search-note">
                        No live matches yet. Try a broader description.
                      </p>
                    )}

                    <p className="hub-ai-results__trust">
                      AI interprets your request. Results come only from live,
                      public GoOurHub sources.
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* 2 · Communities */}
            <section
              ref={panelRef}
              id="hub-panel-community"
              className={`hub-panel hub-panel--communities${
                showAllCommunities ? " is-expanded" : ""
              }`}
              aria-labelledby="hub-communities-title"
              tabIndex={-1}
            >
              <div className="hub-sechead">
                <span
                  className="hub-sechead__ic hub-sechead__ic--purple"
                  aria-hidden="true"
                >
                  <FaGlobeAmericas />
                </span>

                <div className="hub-sechead__tx">
                  <h2 id="hub-communities-title">
                    {hubText.chooseCommunity}
                    {frequentlyVisitedCommunities.length > 0 && (
                      <span className="hub-sechead__count">
                        {frequentlyVisitedCommunities.length}
                      </span>
                    )}
                  </h2>
                  <p>Explore by culture, connect with your community.</p>
                </div>

                <div className="hub-sechead__tools">
                  {/* translates the community list only (not the site) */}
                  <span className="hub-lang" title="Community list language">
                    <LanguageDropdown
                      selectedLanguage={language}
                      onLanguageChange={setLanguage}
                    />
                  </span>

                  {communityCollapsible && (
                    <button
                      type="button"
                      className="hub-sechead__all"
                      aria-expanded={showAllCommunities}
                      onClick={() => setShowAllCommunities((value) => !value)}
                    >
                      {showAllCommunities ? "Show less" : "View all"}
                    </button>
                  )}
                </div>
              </div>

                {language === "english" && (
                  <div aria-live="polite">
                    {communitiesLoading && (
                      <p role="status">Loading live communities...</p>
                    )}

                    {!communitiesLoading && !communitiesError && (
                      <p>
                        
                      </p>
                    )}

                    {!communitiesLoading && communitiesError && (
                      <div role="alert">
                        <p>{communitiesError}</p>
                        <button
                          type="button"
                          className="hub-link-btn hub-link-btn--secondary"
                          onClick={() =>
                            setCommunityReloadNumber((value) => value + 1)
                          }
                        >
                          Try loading live communities again
                        </button>
                      </div>
                    )}
                  </div>
                )}

              <div className="hub-community--desktop">
                <CommunityConnect
                  key={
                    language === "english"
                      ? `live-communities-${directoryCommunities.length}`
                      : `translated-communities-${language}`
                  }
                  countries={communities}
                  frequentlyVisited={shownCommunities}
                  seeAllTile={null}
                />
              </div>

              {/* desktop: fade + full-width bar under the two rows */}
              {communityCollapsible && (
                <div className="hub-more-wrap">
                  <button
                    type="button"
                    className="hub-more"
                    aria-expanded={showAllCommunities}
                    onClick={() => setShowAllCommunities((value) => !value)}
                  >
                    {showAllCommunities
                      ? "Show fewer communities"
                      : `View all ${frequentlyVisitedCommunities.length} communities`}
                    <FaChevronDown aria-hidden="true" />
                  </button>
                </div>
              )}
            </section>

            {/* Community Connect + Post an event. On phones this is the
                two-up row right under the communities card; on desktop
                the same actions live in the right rail. */}
            <HubQuickActions inline />

            {/* 3 · Business categories */}
            <section
              ref={categoryPanelRef}
              id="hub-panel-business"
              className={`hub-panel hub-panel--categories${
                showAllCategories ? " is-expanded" : ""
              }`}
              aria-labelledby="hub-categories-title"
              tabIndex={-1}
            >
              <div className="hub-sechead">
                <span
                  className="hub-sechead__ic hub-sechead__ic--purple"
                  aria-hidden="true"
                >
                  <HiOutlineShoppingBag />
                </span>

                <div className="hub-sechead__tx">
                  <h2 id="hub-categories-title">Business categories</h2>
                  <p>Find local services and support small.</p>
                </div>

                {categoryCollapsible && (
                  <button
                    type="button"
                    className="hub-sechead__all"
                    aria-expanded={showAllCategories}
                    onClick={() => setShowAllCategories((value) => !value)}
                  >
                    {showAllCategories ? "Show less" : "View all"}
                  </button>
                )}
              </div>

                <div aria-live="polite">
                  {categoriesLoading && <p>Loading live categories...</p>}

                  {!categoriesLoading && !categoriesError && (
                    <p>
                      
                    </p>
                  )}

                  {!categoriesLoading && categoriesError && (
                    <div role="alert">
                      <p>
                        {categoriesError} The saved category list is shown so
                        browsing can continue.
                      </p>
                      <button
                        type="button"
                        className="hub-link-btn hub-link-btn--secondary"
                        onClick={() =>
                          setCategoryReloadNumber((value) => value + 1)
                        }
                      >
                        Try loading live categories again
                      </button>
                    </div>
                  )}
                </div>
              <div className="hub-category-config-status">
  <button
    type="button"
    className="hub-link-btn hub-link-btn--secondary"
    onClick={reloadManagedCategories}
    disabled={managedCategoriesLoading}
  >
    {managedCategoriesLoading
      ? "Loading categories..."
      : "Refresh category list"}
  </button>

  {managedCategoriesError && (
    <p role="alert">
      {managedCategoriesError}
      {" "}Existing directory categories are still shown.
    </p>
  )}
</div>
              <ProductCategories
                key={`database-categories-${shownCategoryTiles.length}`}
                searchQuery={searchQuery}
                defaultCategories={shownCategoryTiles}
              />

              {/* desktop: fade + full-width bar under the two rows */}
              {categoryCollapsible && (
                <div className="hub-more-wrap">
                  <button
                    type="button"
                    className="hub-more"
                    aria-expanded={showAllCategories}
                    onClick={() => setShowAllCategories((value) => !value)}
                  >
                    {showAllCategories
                      ? "Show fewer categories"
                      : `View all ${allCategoryTiles.length} categories`}
                    <FaChevronDown aria-hidden="true" />
                  </button>
                </div>
              )}
            </section>

            {/* 4 · Local deals & finds */}
            <HubHappening />

            {/* 5 · Upcoming events (renders nothing until one exists) */}
            <HubEventsRail />

            {/* 6 · Nonprofits & community orgs */}
            <HubOrgs />

            {/* 6 · Popular businesses this week (phones) */}
            {isMobile && popularBusinesses.length > 1 && (
              <section
                className="hub-discover hub-discover--popular"
                aria-labelledby="hub-popular-title"
              >
                <div className="hub-sechead">
                  <span
                    className="hub-sechead__ic hub-sechead__ic--gold"
                    aria-hidden="true"
                  >
                    <FaStar />
                  </span>

                  <div className="hub-sechead__tx">
                    <h2 id="hub-popular-title">Popular businesses this week</h2>
                    <p>Picks from the GoOurHub community.</p>
                  </div>
                </div>

                <div className="hub-pop">
                  {popularBusinesses.map((business, index) => (
                    <button
                      key={business.id || `${businessDisplayName(business)}-${index}`}
                      type="button"
                      className="hub-pop__card"
                      onClick={() => openBusinessResult(business)}
                    >
                      <DiscoverThumb
                        business={business}
                        className={`hub-pop__th ci-ph-${index % 4}`}
                      >
                        {discoverPremium(business) && (
                          <span className="hub-pop__star">★</span>
                        )}
                      </DiscoverThumb>

                      <span className="hub-pop__bd">
                        <span className="hub-pop__nm">
                          {businessDisplayName(business)}
                        </span>
                        <span className="hub-pop__tg">
                          <FaMapMarkerAlt aria-hidden="true" />
                          {[
                            businessCategoryName(business),
                            firstText(business?.city, "San Antonio"),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* 7 · Recently added businesses (phones) */}
            {isMobile && newestBusinesses.length > 0 && (
              <section
                className="hub-discover hub-discover--recent"
                aria-labelledby="hub-recent-title"
              >
                <div className="hub-sechead">
                  <span
                    className="hub-sechead__ic hub-sechead__ic--new"
                    aria-hidden="true"
                  >
                    NEW
                  </span>

                  <div className="hub-sechead__tx">
                    <h2 id="hub-recent-title">Recently added businesses</h2>
                    <p>New to GoOurHub.</p>
                  </div>
                </div>

                <div className="ci-rows">
                  {newestBusinesses.map((business, index) => (
                    <div
                      key={business.id || `${businessDisplayName(business)}-new-${index}`}
                      className="ci-row"
                    >
                      <button
                        type="button"
                        className="ci-row__main"
                        onClick={() => openBusinessResult(business)}
                      >
                        <DiscoverThumb
                          business={business}
                          className={`ci-row__th ci-ph-${(index + 2) % 4}`}
                        />

                        <span className="ci-row__bd">
                          <span className="ci-row__nm">
                            {businessDisplayName(business)}
                          </span>
                          <span className="ci-row__tg">
                            {[
                              businessCategoryName(business),
                              firstText(business?.city),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </span>
                      </button>

                      <span className="ci-row__go" aria-hidden="true">
                        <FaArrowRight />
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* desktop: business-owner bar (hidden on phones) */}
            <section className="hub-actions" aria-label="Business owner actions">
              <span className="hub-actions__label">Own a business here?</span>

              <div className="hub-actions__buttons">
                <Link
                  to="/partner-signup"
                  className="hub-link-btn hub-link-btn--submit"
                >
                  List your business
                  <FaArrowRight aria-hidden="true" />
                </Link>
              </div>
            </section>

            {/* 8 · phones: Add your business + Shop HubMart */}
            <div className="hub-cta-card" aria-label="Get started">
              <button
                type="button"
                className="hub-cta hub-cta--gold"
                onClick={goToPartnerSignup}
              >
                <span className="hub-cta__ic" aria-hidden="true">
                  <HiOutlineBuildingStorefront />
                </span>
                <span className="hub-cta__tx">
                  <strong>Add your business</strong>
                  <span>Get listed and grow your reach.</span>
                </span>
                <FaChevronRight className="hub-cta__go" aria-hidden="true" />
              </button>

              <Link to="/HubMart" className="hub-cta hub-cta--purple">
                <span className="hub-cta__ic" aria-hidden="true">
                  <HiOutlineShoppingBag />
                </span>
                <span className="hub-cta__tx">
                  <strong>Shop HubMart</strong>
                  <span>Buy from local. Support local.</span>
                </span>
                <FaChevronRight className="hub-cta__go" aria-hidden="true" />
              </Link>
            </div>

            {selectedBusiness && (
              <div
                className="ci-sheetwrap"
                onClick={() => setSelectedBusiness(null)}
              >
                <div
                  className="ci-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-label={businessDisplayName(selectedBusiness)}
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className="ci-sheet__x"
                    onClick={() => setSelectedBusiness(null)}
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>

                  <h3 className="ci-sheet__nm">
                    {businessDisplayName(selectedBusiness)}
                  </h3>

                  {firstText(
                    selectedBusiness?.address,
                    selectedBusiness?.full_address
                  ) && (
                    <p className="ci-sheet__row">
                      <strong>Address:</strong>{" "}
                      {firstText(
                        selectedBusiness?.address,
                        selectedBusiness?.full_address
                      )}
                    </p>
                  )}

                  {firstText(
                    selectedBusiness?.phoneNumber,
                    selectedBusiness?.phone_number,
                    selectedBusiness?.phone
                  ) && (
                    <p className="ci-sheet__row">
                      <strong>Phone:</strong>{" "}
                      {firstText(
                        selectedBusiness?.phoneNumber,
                        selectedBusiness?.phone_number,
                        selectedBusiness?.phone
                      )}
                    </p>
                  )}

                  <button
                    type="button"
                    className="ci-sheet__takeme"
                    onClick={() => openBusinessMap(selectedBusiness)}
                  >
                    <FaMapMarkerAlt /> Take Me There
                  </button>

                  {sheetProfilePath ? (
                    <button
                      type="button"
                      className="ci-sheet__site"
                      onClick={() => {
                        setSelectedBusiness(null);
                        navigate(sheetProfilePath);
                      }}
                    >
                      View full profile
                    </button>
                  ) : (
                    sheetWebsite && (
                      <a
                        className="ci-sheet__site"
                        href={sheetWebsite}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {sheetWebsiteRaw}
                      </a>
                    )
                  )}

                  <div className="ci-sheet__acts">
                    <button
                      type="button"
                      className={`ci-sheet__act ${
                        isSaved(selectedBusiness?.id) ? "is-on" : ""
                      }`}
                      onClick={() =>
                        toggleSave(
                          selectedBusiness?.id,
                          businessDisplayName(selectedBusiness)
                        )
                      }
                      aria-pressed={isSaved(selectedBusiness?.id)}
                    >
                      {isSaved(selectedBusiness?.id) ? (
                        <FaHeart />
                      ) : (
                        <FaRegHeart />
                      )}{" "}
                      {isSaved(selectedBusiness?.id) ? "Saved" : "Save"}
                    </button>

                    <button
                      type="button"
                      className="ci-sheet__act"
                      onClick={() => shareBusinessFromHub(selectedBusiness)}
                    >
                      <FaShareAlt /> Share
                    </button>

                    <button
                      type="button"
                      className="ci-sheet__act"
                      onClick={() => setSelectedBusiness(null)}
                    >
                      Close
                    </button>
                  </div>

                  <p className="ci-sheet__disc">
                    Disclaimer: This listing has not been claimed or verified
                    by the business owner. Information provided is based on
                    publicly available sources.
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>

        <div className="rightNavSection">
          <div className="hub-rail--desktop">
            <SpotlightRail />
          </div>
        </div>

        <Footer />
      </Template>
    </div>
  );
}
